"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.symbolResourceFile = symbolResourceFile;
exports.previewResourceFile = previewResourceFile;
exports.documentIsOriginal = documentIsOriginal;
exports.documentBehaviorIsDerived = documentBehaviorIsDerived;
exports.documentBehaviorHasErrors = documentBehaviorHasErrors;
exports.getExtractedElementDataWithoutExpressionEvaluation = getExtractedElementDataWithoutExpressionEvaluation;
exports.getExtractedElementsWithoutExpressionEvaluation = getExtractedElementsWithoutExpressionEvaluation;
exports.getTerminalPosition = getTerminalPosition;
exports.getTerminalType = getTerminalType;
exports.getPropertyByName = getPropertyByName;
exports.getPropertyByKey = getPropertyByKey;
exports.getDocumentPropertyByName = getDocumentPropertyByName;
exports.getTerminals = getTerminals;
exports.unbakedPcbLayoutNode = unbakedPcbLayoutNode;
exports.getUnbakedPcbLayoutNodes = getUnbakedPcbLayoutNodes;
exports.documentIsPublished = documentIsPublished;
exports.documentHasSimModel = documentHasSimModel;
exports.documentHasBehaviour = documentHasBehaviour;
const constants_1 = require("@buildwithflux/constants");
const lodash_1 = require("lodash");
const functions_1 = require("../part/functions");
const property_1 = require("../property");
/**
 * Get the (remote) path to the symbol that represents this document.  The symbol is what users will see when
 * they add the document as a part on their canvas. It will try to determine what the right
 * path is by examining the assets. If no symbol is set, it will return an empty string which will cause the UI
 * to display create a default symbol.
 * @returns Either the path to the symbole resource fileor an empty string.
 * @remarks Note that this function is O(N) in the length of the assets worst case.
 * @privateRemarks TODO: why the empty string?  Is that actually useful?  Will it ever return that value?  Should
 * we return something stronger?
 */
function symbolResourceFile(documentAssets) {
    return Object.values(documentAssets).find((assetData) => assetData.isDefault)?.storageName ?? "";
}
/**
 * Get the (remote) path to the preview image of this document.
 *
 * The preview image is what users will see in PartLibrary when this document is published and displayed there.
 * If no preview image is set, it will try to determine what the right path is by examining the assets
 * @returns Either the path to the preview image or an empty string.
 * @remarks Note that this function is O(N) in the length of the assets worst case.
 */
function previewResourceFile(previewImage, documentAssets) {
    return previewImage ?? Object.values(documentAssets).find((assetData) => assetData.isDefault)?.storageName ?? "";
}
/**
 * Check if a document originated as a fork or clone of another document.
 * @returns true if the document is not a fork or clone.
 */
function documentIsOriginal(document) {
    return !document.copy_of_document_uid && !document.fork_of_document_uid;
}
/**
 * True if all parts in the document have a behavior, excluding the base case of only system
 * parts.
 *
 * NOTE: Missing data was observed in the wild contrary to type definitions.
 */
function isBehaviourDerived(elements) {
    if (!elements.length)
        return false;
    const partDatas = elements.map((element) => element.part_version_data_cache);
    const partUids = partDatas.map((pd) => pd.part_uid);
    if (!(0, lodash_1.every)(partDatas) ||
        !(0, lodash_1.every)(partUids) ||
        (0, lodash_1.every)(partUids, functions_1.isSystemPart) // base case
    ) {
        return false;
    }
    else {
        return (0, lodash_1.every)(partDatas, functions_1.partHasSimModel);
    }
}
function documentBehaviorIsDerived(documentElements) {
    return isBehaviourDerived(Object.values(documentElements));
}
function documentBehaviorHasErrors(document) {
    return document.behaviour_has_errors ?? false;
}
function getDefaultPartProperty(properties) {
    let defaultProperty;
    Object.values(properties ?? {})
        .sort()
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        .forEach((property) => {
        const typeProperty = Object.values(constants_1.defaultPropertyDefinitions).find((p) => {
            return p.type === constants_1.PropertyTypes.value && p.label === property.name;
        });
        if (typeProperty && !defaultProperty) {
            defaultProperty = property;
        }
    });
    return defaultProperty;
}
function extractPropertiesWithoutEvaluation(properties) {
    return (0, lodash_1.mapValues)(properties, (p) => ({ name: p.name, value: p.value }));
}
function getExtractedElementDataWithoutExpressionEvaluation(element) {
    const defaultProperty = getDefaultPartProperty(element.properties);
    return {
        label: element.label,
        defaultPropertyValue: defaultProperty?.value.toString(),
        properties: extractPropertiesWithoutEvaluation(element.properties),
        diagram_position: element.diagram_position,
        document_import_uid: element.part_version_data_cache.document_import_uid,
        part_version_data_cache: {
            name: element.part_version_data_cache.name,
            extracted_element_cache: element.part_version_data_cache.extracted_element_cache,
            pcbLayoutRuleSets: element.part_version_data_cache.pcbLayoutRuleSets,
            part_uid: element.part_version_data_cache.part_uid,
            version: element.part_version_data_cache.version,
            assets: element.part_version_data_cache.assets,
        },
        designatorMappings: element.designatorMappings,
    };
}
function getExtractedElementsWithoutExpressionEvaluation(documentElements) {
    const result = {};
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    Object.entries(documentElements).forEach(([elementId, element]) => {
        result[elementId] = getExtractedElementDataWithoutExpressionEvaluation(element);
    });
    return result;
}
/**
 * Get the position of a terminal on the canvas.
 */
function getTerminalPosition(element) {
    const canvasPinPositionValue = getPropertyByName(element, constants_1.defaultPropertyDefinitions.symbol_pin_position.label)?.value;
    if (!canvasPinPositionValue)
        return undefined;
    const vector2Result = property_1.VectorObjectValue2.safeParse(canvasPinPositionValue);
    if (vector2Result.success) {
        return vector2Result.data.data;
    }
    const vector3Result = property_1.VectorObjectValue3.safeParse(canvasPinPositionValue);
    if (vector3Result.success) {
        return vector3Result.data.data;
    }
    return undefined;
}
/**
 * Get the type of a terminal.
 * @param elementData The element, presumed here to be of terminal type.
 * @returns Either the property value if found, or undefined if no such property is set.
 * QUESTION: why take the part type if no terminal type is found?
 */
function getTerminalType(elementData) {
    const pinTypeByKey = getPropertyByKey(elementData, constants_1.defaultPropertyDefinitions.pin_type.key);
    if (pinTypeByKey)
        return pinTypeByKey;
    return getPropertyByKey(elementData, constants_1.defaultPropertyDefinitions.part_type.key);
}
/**
 * Get a property from an element by name.
 * @param element The element to be interrogated
 * @param propertyName The name of the property to be found
 * @returns Either the data associated with the property, or undefined if the property is not set on the element.
 * @privateRemarks QUESTION: could we improve the typing here?  Feels like we could.
 */
function getPropertyByName(element, propertyName) {
    return Object.values(element.properties).find((property) => property.name === propertyName);
}
/**
 * Get a property by its key.  Only works for system properties.
 * @param element The element to be interrogated.
 * @param key The property key
 * @returns Either the property data, or undefined if it isn't set on the element.
 * @remarks It is not an error to call this function with a key that isn't a system key.
 * @privateRemarks TODO: We could definitely make the typing stronger here!
 * QUESTION: why get by find? why not by lookup?
 */
function getPropertyByKey(element, propertyKey) {
    return Object.values(element.properties).find((property) => property.key === propertyKey);
}
function getDocumentPropertyByName(document, propertyName) {
    return Object.values(document.properties).find((property) => property.name === propertyName);
}
/*
 * Get all terminals from a map of elements.
 */
function getTerminals(documentElements, includeProperties = false) {
    const terminals = {};
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    Object.values(documentElements).forEach((elementData) => {
        const partType = (0, functions_1.getPartType)(elementData.part_version_data_cache);
        if ((0, functions_1.matchesPartType)(partType, constants_1.partPropertyCategories.terminal, { includeParents: false })) {
            const terminalOrderProperty = getPropertyByName(elementData, constants_1.defaultPropertyDefinitions.terminal_order.label);
            const terminalPinNumberProperty = getPropertyByName(elementData, constants_1.defaultPropertyDefinitions.pin_number.label);
            // IDEA: we could do much better than this by actually defining the types on properties more rigorously and parsing them.
            const order = terminalOrderProperty?.value;
            const pinNumber = terminalPinNumberProperty?.value;
            const terminal = {
                uid: elementData.uid,
                name: elementData.label ?? "",
                type: getTerminalType(elementData)?.value ?? "",
            };
            if (includeProperties) {
                terminal.properties = elementData.properties;
            }
            if (order) {
                terminal.order = order;
            }
            if (pinNumber) {
                terminal.pin_number = pinNumber;
            }
            terminal.position = getTerminalPosition(elementData);
            terminals[terminal.uid] = terminal;
        }
    });
    return terminals;
}
/**
 * Creates a copy of a node with all of its baking undone - i.e. there are no baked or flattened rules.
 *
 * TODO: [NodeType] - better way to implement this? Right now using all these `as any` cast to bypass
 * compiler type checking.
 */
function unbakedPcbLayoutNode(node) {
    const unbakedNode = { ...node };
    delete unbakedNode.bakedRules;
    delete unbakedNode.flattenedRules;
    delete unbakedNode.childUids;
    delete unbakedNode.virtualNodeInfo;
    return unbakedNode;
}
/**
 * Get a copy of all of the pcbLayoutNodes from a document in their "unbaked" state, with no baked or flattened
 * rules.
 */
function getUnbakedPcbLayoutNodes(document) {
    const pcbLayoutNodes = {};
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    Object.values(document.pcbLayoutNodes).forEach((node) => {
        // NOTE: From typing, this doesn't really make sense, but I understand this as a safe step
        // to make sure the result `pcbLayoutNodes` (from document state) is unbaked
        pcbLayoutNodes[node.uid] = unbakedPcbLayoutNode(node);
    });
    return pcbLayoutNodes;
}
/**
 * Check to see if a document is published and will be visible in the library.
 * QUESTION: do we really want to consider falsy values as published?
 *
 * @param document At least a base document.  Does not rely on any nested data.
 * @returns True if the document is a part i.e. is published.
 */
function documentIsPublished(document) {
    return document.belongs_to_part_uid !== "";
}
/**
 * Whether a document has a complete set of behaviours that can be used by the
 * usercode_runtime. For example, in the following nested hierarchy, the topmost
 * parents has a sim model by derivation.
 *
 *   -parent: no behaviour
 *   --child: behaviour
 *   --child: behaviour
 *   --parent: no behaviour
 *   ---child: behaviour
 *   ---child: behaviour
 *
 * NOTE: the user code may not include a model config in which case we get a
 * false positive.
 */
function documentHasSimModel(document) {
    return documentHasBehaviour(document) || documentBehaviorIsDerived(document.elements) || false;
}
/**
 * Whether a document has user code, minus comments, and without errors.
 */
function documentHasBehaviour(document) {
    return !!removeJsComments(document.behaviour ?? "").trim() && !documentBehaviorHasErrors(document);
}
function removeJsComments(jsCode) {
    return jsCode.replace(/\/\*[\s\S]*?\*\/|\/\/.*/g, "");
}
//# sourceMappingURL=functions.js.map